import {
  FASCIA_SENZA_TETTO,
  SBARRAMENTO_ASSENTE,
  resolveTimeControl,
  type OpeningMode,
} from '@draughts/shared';
import type { TFunction } from 'i18next';
import { decimale } from './formato-data';
import { pastigliaApertura } from './opening-labels';

/**
 * I punti di un torneo come li scrive una classifica vera: `1½`, non `1.5`.
 *
 * ⚠️ Sulla scala di oggi — **2 / 1 / 0** (`SWISS_SCORING`), col riposo a 2 —
 * ogni totale e' un INTERO e questa funzione si limita a stamparlo. Il mezzo
 * punto resta gestito per due casi, non per il caso normale:
 *
 *  1. i **dati storici** dei tornei giocati prima del passaggio di scala che
 *     una migration non avesse raddoppiato;
 *  2. un **formato futuro** che tornasse a usare frazioni.
 *
 * Per questo non e' stata cancellata quando la scala e' cambiata: e' una rete,
 * e una rete si giudica da cosa trattiene, non da quanto spesso si tende.
 *
 * La frazione vulgare e' la convenzione di qualunque tabellone di dama o di
 * scacchi, e risolve da sola un problema che il numero grezzo aveva:
 * `{player.points}` stampava `1.5` col PUNTO in tutte e otto le lingue, mentre
 * dodici pixel piu' sotto Buchholz e Sonneborn-Berger scrivevano `1,5` con la
 * virgola, perche' passano da `decimale()`. Due separatori decimali nella
 * stessa riga.
 *
 * `½` e' un carattere solo (U+00BD) e non una parola: non si traduce, e occupa
 * meno di «,5». Con `tabular-nums` sulla colonna, `1` e `1½` restano
 * incolonnati.
 *
 * Mezzo punto SENZA intero si scrive `½` e non `0½`: e' cosi' che si legge su
 * un tabellone.
 *
 * Il ripiego su `decimale` non e' teorico: se un domani un formato nuovo
 * assegnasse terzi di punto, o un dato arrivasse sporco, questa funzione
 * scriverebbe un numero FALSO arrotondando in silenzio. Meglio un `1,3` che
 * stona che un `1½` che mente.
 *
 * La usa anche il punteggio degli incontri a due manche
 * (`RematchPanel`, `ReturnLegCountdown`), che prima aveva due copie locali
 * sbagliate: il nome dice «torneo» per la sua origine, non per un confine.
 */
export function puntiTorneo(punti: number): string {
  if (!Number.isFinite(punti)) return '0';
  const doppio = punti * 2;
  if (!Number.isInteger(doppio)) return decimale(punti);
  const interi = Math.floor(punti);
  const mezzo = doppio % 2 !== 0;
  if (!mezzo) return String(interi);
  return interi === 0 ? '½' : `${interi}½`;
}

/** Il trattino al posto del posto: «—» (U+2014), non il meno di tastiera. */
export const POSTO_MUTO = '—';

/**
 * Nessuno ha ancora giocato, quindi il posto in classifica non dice niente.
 *
 * Prima dell'avvio tutti pareggiano su tutti i criteri sportivi, e il server —
 * correttamente — li numera tutti PRIMI (`numeraConExAequo`, lato server): un
 * ex aequo fra gente che non ha giocato e' vero, ed e' mostrarlo come
 * posizione che non lo e'. A venti pre-iscritti si leggeva «#1» in venti.
 * Nell'arena la bugia e' l'altra: li' le posizioni escono da un `ROW_NUMBER()`
 * ordinato per `user_id`, quindi pre-avvio il podio lo decidono gli UUID.
 *
 * E' la stessa trappola di `spareggiCalcolati` in `TournamentStandings` — un
 * numero che si legge proprio quando non significa niente — e la risposta e'
 * la stessa: non scriverlo finche' non c'e'.
 *
 * `=== 0` e non `?? 0`: il campo manca solo nei payload che non lo portano, e
 * quello vuol dire «non si sa», non «nessuno ha giocato». Nel dubbio il posto
 * si mostra. (Il DTO lo manda sempre: `gamesPlayed: s.games_played`.)
 *
 * Si guarda TUTTA la classifica e non la singola riga: e' una scelta della
 * tabella, non della riga, e a meta' farebbe righe disuguali.
 */
export function nessunoHaGiocato(righe: readonly { gamesPlayed?: number }[]): boolean {
  return righe.length > 0 && righe.every((r) => r.gamesPlayed === 0);
}

/**
 * Il nome leggibile di una cadenza: `'blitz-3+1'` → «Blitz 3+1».
 *
 * Indicizzava `TIME_CONTROL_PRESETS`, che conosce le DICIANNOVE cadenze con
 * un nome e nient'altro. Ma tornei e arene si aprono anche su una cadenza
 * scelta a mano, e per quelle il ripiego stampava la CHIAVE GREZZA: sulla
 * riga di un torneo a tre minuti con uno di incremento si leggeva
 * `blitz-3+1`, dove la card di un torneo accanto diceva «Blitz 5+3» — stessa
 * colonna, stessa pagina, due alfabeti.
 *
 * `resolveTimeControl` e' la funzione che lo sa fare, e la sua docstring lo
 * dice per esteso: vale per tutte e diciannove (riproduce la tabella esatta,
 * lo provano i suoi test) e in piu' per ogni cadenza della griglia.
 *
 * Il ripiego sulla chiave resta per cio' che quella griglia non ammette — una
 * chiave sporca o inventata: meglio una stringa strana che una casella vuota.
 */
export function getTimeControlName(preset: string): string {
  return resolveTimeControl(preset)?.name || preset;
}

/** Calculate progress percentage for an active tournament/arena */
export function calculateProgress(startsAt: string, endsAt: string): number {
  const now = Date.now();
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  const total = end - start;
  const elapsed = now - start;
  return Math.min(Math.max((elapsed / total) * 100, 0), 100);
}

/**
 * La fine di un'arena, anche quando il database non l'ha ancora scritta.
 *
 * `ends_at` viene stampata all'avvio, ma le arene partite prima che il server
 * lo facesse hanno la colonna vuota fino alla chiusura: finché manca, la fine
 * si ricava dall'inizio più la durata. Senza questo ripiego un'arena in corso
 * non ha né clessidra né barra di avanzamento.
 */
export function effectiveEndsAt(arena: {
  endsAt: string | null;
  startsAt: string;
  durationMinutes: number;
}): string | null {
  if (arena.endsAt) return arena.endsAt;
  if (!arena.startsAt || !arena.durationMinutes) return null;
  return new Date(new Date(arena.startsAt).getTime() + arena.durationMinutes * 60 * 1000).toISOString();
}

/**
 * L'arena ha finito il suo tempo ma non e' ancora chiusa.
 *
 * Allo scadere dell'orario un'arena NON si chiude se ci sono partite in corso:
 * quelle si giocano fino in fondo, i loro punti valgono, e la classifica finale
 * esce solo con l'ultima. In quella finestra la riga a database resta
 * `'active'` — ma non si accoppia piu' nessuno, non si riprende la ricerca e
 * non ci si iscrive: lato server e' `arenaScaduta` in `arena.constants.ts`.
 *
 * Qui il traguardo e' `effectiveEndsAt`, cioe' `endsAt` quando c'e', mentre il
 * server somma sempre `starts_at + duration`. L'asimmetria e' voluta: questo e'
 * lo stesso istante che la clessidra della banda del tempo sta contando dieci
 * pixel piu' su, e una frase che litiga con l'orologio che le sta accanto e'
 * peggio di una frase che arriva un secondo prima o dopo.
 *
 * L'orologio e' quello del dispositivo, e puo' sbagliare: chi l'ha avanti
 * nasconde in anticipo un bottone che il server onorerebbe ancora (fastidio,
 * mai un danno); chi l'ha indietro preme e il server rifiuta con
 * `ARENA_SCADUTA`, che lo store sa rimettere a posto. Per questo le due
 * guardie convivono, e nessuna delle due e' di troppo.
 */
export function arenaScadutaMaAttiva(
  arena: { status: string; endsAt: string | null; startsAt: string; durationMinutes: number },
  adesso: number = Date.now(),
): boolean {
  if (arena.status !== 'active') return false;
  const fine = effectiveEndsAt(arena);
  if (fine === null) return false;
  const traguardo = new Date(fine).getTime();
  return !Number.isNaN(traguardo) && adesso >= traguardo;
}

/*
 * QUI C'ERA `getStatusConfig`, che dava testo e classe al gettone dello stato
 * delle due card dell'elenco — «IN CORSO / IN ARRIVO / CONCLUSO».
 *
 * I due gettoni sono andati via insieme (20 ottobre 2026): l'elenco e'
 * SEGMENTATO per stato, tre linguette, e ogni card che si vede sta nella
 * scheda del suo stato — la pastiglia ripeteva la linguetta aperta. Le pagine
 * di dettaglio hanno i loro ternari e la loro pastiglia
 * (`tournament-status-badge-detail`, `arena-status-badge-detail`): non
 * passavano da qui.
 *
 * Due dei suoi rami erano anche irraggiungibili dall'elenco, che filtra a
 * `scheduled | active | finished`: `cancelled` e il `default` per uno stato
 * sconosciuto. La chiave `tournament.status.cancelled` resta nelle otto
 * lingue, per il giorno in cui un torneo annullato si dovra' nominare di
 * nuovo.
 */

/**
 * Come si chiama il formato di un torneo, in una pastiglia.
 *
 * Tre casi e non due, ed e' il punto di tutto il lavoro sull'italo-svizzero:
 * finche' il torneo e' PROGRAMMATO e porta una soglia, il suo `format` non e'
 * ancora una decisione presa — e' uno svizzero di difetto, che all'ora
 * d'inizio potrebbe retrocedere a girone contando gli iscritti. Annunciarlo
 * come «Svizzero» sarebbe una promessa che il server non ha fatto.
 *
 * Dall'avvio in poi il formato c'e' davvero, e si dice il suo nome: e' anche
 * l'unica cosa che chi guarda la classifica ha bisogno di sapere.
 *
 * In UN posto solo perche' lo leggono in tre — la card dell'elenco, la sala e
 * la pagina del dettaglio — e tre copie della stessa condizione sono tre
 * occasioni di ripararne due.
 *
 * ⚠️ Risponde una CHIAVE, non una parola: le pastiglie hanno la forma corta
 * («Svizzero», non «Sistema svizzero»), perche' a 360px trenta caratteri
 * mandano a capo la riga delle pastiglie.
 */
/**
 * Come si chiama il formato di un'ARENA, in una pastiglia.
 *
 * Una costante e non una funzione, perche' un'arena e' un'arena: non c'e'
 * niente da decidere, al contrario del torneo qui sotto, dove il nome dipende
 * dallo stato e dalla soglia.
 *
 * Sta qui, accanto alla sua gemella, perche' la leggono DUE schermate — la
 * riga «Tornei attivi» della home e la card dell'arena nell'elenco — e la
 * stessa chiave scritta a mano in due posti e' il modo in cui due schermate
 * finiscono a chiamare la stessa cosa con due nomi.
 */
export const CHIAVE_FORMATO_ARENA = 'tournament.formats.arenaShort';

export function chiaveFormatoTorneo(torneo: {
  format?: string | null;
  status?: string;
  swissThreshold?: number | null;
}): string {
  if (torneo.status === 'scheduled' && torneo.swissThreshold != null) {
    return 'tournament.formats.italoSwissShort';
  }
  switch (torneo.format) {
    case 'round_robin': return 'tournament.formats.roundRobinShort';
    case 'swiss': return 'tournament.formats.swissShort';
    default: return 'tournament.formats.arenaShort';
  }
}

/* ── I FATTI DI UNA GARA, e le regole di quando si dicono ─────────────────
 *
 * Una card diceva formato, stato, cadenza, durata, variante, iscritti e quando
 * comincia. Tutto il RESTO delle regole con cui si gioca — l'apertura, lo
 * sbarramento, l'andata e ritorno, i posti, la soglia dell'italo-svizzero, il
 * turno extra — o non usciva dal server, o usciva e non lo disegnava nessuno.
 * Chi scorreva l'elenco non aveva gli elementi per scegliere a quale torneo
 * iscriversi, e in due casi (lo sbarramento, il minimo di iscritti) scopriva la
 * regola solo quando «Iscriviti» diceva di no.
 *
 * Le regole stanno QUI e non nei componenti per due ragioni, e nessuna delle
 * due e' il riuso:
 *
 *  1. la card e la pagina di dettaglio devono dire la STESSA cosa. Due copie
 *     delle stesse condizioni sono due occasioni di ripararne una sola, e il
 *     sintomo sarebbe un elenco che promette un torneo diverso da quello che la
 *     sua pagina descrive;
 *  2. «quando si dice un fatto» e' la decisione di prodotto di tutto questo
 *     lavoro, ed e' la cosa che marcisce. Funzioni pure si provano senza
 *     montare un componente, quindi la prova resta leggibile e sopravvive al
 *     prossimo rifacimento della grafica.
 */

/** Il tipo minimo che serve per decidere: ci entrano sia la riga dell'elenco
 *  sia il corpo del dettaglio, senza che nessuna delle due debba conoscere
 *  l'altra. */
export interface FattiDiUnaGara {
  status?: string;
  minRating?: number | null;
  maxRating?: number | null;
  minPlayers?: number | null;
  maxPlayers?: number | null;
  swissThreshold?: number | null;
  swissExtraRounds?: number;
  twoLeg?: boolean;
  doubleRound?: boolean;
  openingMode?: OpeningMode | null;
  /** Quale apertura, con `openingMode: 'single'`: il numero di tabella F.I.D. */
  openingId?: number | null;
  variant?: string | null;
}

/** Una cella della griglia: un'etichetta e un valore, come le tre che ci sono
 *  gia' (cadenza, durata, variante). */
export interface CellaFatto {
  etichetta: string;
  valore: string;
  testid: string;
}

/** Una pastiglia della riga delle condizioni: solo un testo. */
export interface Condizione {
  testo: string;
  testid: string;
}

/**
 * Quante celle FACOLTATIVE sta una card, oltre alle tre fisse.
 *
 * Non e' una potatura di routine: le tre facoltative coesistono solo su un
 * italo-svizzero programmato CON sbarramento, che e' raro. E' una rete contro
 * il caso in cui la lista raddoppi d'altezza — sei celle su ognuna di decine di
 * card sono due righe di griglia in piu' ciascuna — e cio' che viene tagliato
 * si trova comunque INTERO sulla pagina di dettaglio, che chiama con
 * `tetto: Infinity`. Il costo di sbagliare qui e' l'altezza dell'elenco, non
 * uno sfondamento a 360px.
 */
export const TETTO_CELLE_CARD = 2;

/**
 * Le celle dei fatti numerici, gia' in ordine di PRIORITA'.
 *
 * L'ordine non e' estetico, e' quello che regge il taglio del tetto: prima cio'
 * che puo' impedirti di entrare, poi cio' che decide *cosa* giocherai, infine
 * cio' che decide *se* si gioca.
 */
/**
 * LA CELLA DELLO SBARRAMENTO, da sola e fuori da `celleDeiFatti`.
 *
 * Sta in una funzione sua perche' la leggono TRE superfici e non due: la card
 * dello svizzero e la pagina del torneo la prendono dentro `celleDeiFatti`,
 * mentre l'ARENA vuole questa e solo questa — le altre celle (posti, soglia,
 * turno extra) non c'entrano con lei e comparirebbero su una card che nessuno
 * ha chiesto di cambiare. Estrarla e' l'alternativa a riscrivere la condizione
 * e le tre forme del valore in `ArenaCard`, cioe' a due posti da cui lo stesso
 * numero potrebbe uscire scritto in due modi.
 *
 * `null` e non una cella vuota: «questa gara non ha sbarramento» e' la risposta
 * di quasi tutte, e chi chiama deve poterla saltare senza guardare dentro.
 *
 * Solo quando c'e': zero vuol dire «nessuno sbarramento» ed e' il caso di quasi
 * tutti i tornei — una cella «0» sarebbe una regola annunciata che non esiste.
 * E 9999 e' il DEFAULT storico di `max_rating` (migration 003, e la 011 per le
 * arene), non un tetto che qualcuno abbia scelto.
 *
 * ⚠️ Nessun controllo su `isRated`: i due controller azzerano gia' il numero
 * sulle gare non valutate, quindi la condizione sullo zero copre anche quel
 * caso e un secondo controllo sarebbe la stessa regola scritta due volte —
 * cioe' due posti da cui potrebbe divergere.
 *
 * In OGNI stato, al contrario delle celle che la seguono in `celleDeiFatti`: su
 * un torneo concluso resta vero che si giocava fra 1500 e 1800, ed e' come
 * leggere il livello di un archivio.
 */
export function cellaSbarramento(
  t: TFunction,
  gara: Pick<FattiDiUnaGara, 'minRating' | 'maxRating'>,
  { etichetteLunghe = false }: { etichetteLunghe?: boolean } = {},
): CellaFatto | null {
  const pavimento = gara.minRating ?? SBARRAMENTO_ASSENTE;
  const tettoPunteggio = gara.maxRating ?? FASCIA_SENZA_TETTO;
  const haPavimento = pavimento > SBARRAMENTO_ASSENTE;
  const haTetto = tettoPunteggio < FASCIA_SENZA_TETTO;
  if (!haPavimento && !haTetto) return null;
  /* Tre forme e non una: «1500+» e' un pavimento, «≤1800» un tetto, e solo
     insieme sono una fascia. Segni e non parole — si leggono uguale in tutte
     e otto le lingue, e una chiave i18n per un `+` sarebbe otto file. */
  const valore = haPavimento && haTetto
    ? `${pavimento}–${tettoPunteggio}`
    : haPavimento ? `${pavimento}+` : `≤${tettoPunteggio}`;
  return {
    etichetta: t(etichetteLunghe ? 'tournament.detail.ratingFloor' : 'tournament.create.ratingFloorLabel'),
    valore,
    testid: 'tournament-card-sbarramento',
  };
}

export function celleDeiFatti(
  t: TFunction,
  gara: FattiDiUnaGara,
  { tetto = TETTO_CELLE_CARD, etichetteLunghe = false }: { tetto?: number; etichetteLunghe?: boolean } = {},
): CellaFatto[] {
  const celle: CellaFatto[] = [];
  const programmato = gara.status === 'scheduled';

  /*
   * 1. LO SBARRAMENTO, ed e' il primo perche' e' l'unico fatto che puo'
   * trasformare «Iscriviti» in un rifiuto (`RIFIUTO_SBARRAMENTO`, lato
   * server). Va detto dove la decisione si prende, cioe' nell'elenco.
   *
   * Il corpo sta in `cellaSbarramento` qui sopra, perche' l'arena vuole quella
   * cella SENZA le altre: il perche' e le condizioni sono scritti la'.
   */
  const sbarramento = cellaSbarramento(t, gara, { etichetteLunghe });
  if (sbarramento) celle.push(sbarramento);

  /*
   * 2. LA SOGLIA dell'italo-svizzero — e la condizione e' la STESSA con cui
   * `chiaveFormatoTorneo` qui sopra scrive «Italo-svizzero»: questa cella
   * SPIEGA quella pastiglia, e le due non possono divergere perche' leggono lo
   * stesso paio di campi.
   *
   * Solo finche' il torneo e' programmato: dall'avvio in poi il formato e'
   * deciso davvero (`risolviFormatoAllAvvio`) e `format` dice il vero;
   * mostrare ancora la soglia contraddirebbe il gettone che le sta accanto.
   *
   * Il numero NUDO, senza il «+» del pavimento qui sopra: il verso lo dice gia'
   * l'etichetta («Svizzero da»), e un segno in piu' davanti a quella sarebbe un
   * secondo «da» disegnato. Stessa misura, e stesso motivo, del menu di
   * creazione, che offre le stesse voci.
   */
  if (programmato && gara.swissThreshold != null) {
    celle.push({
      etichetta: t('tournament.create.thresholdLabel'),
      valore: String(gara.swissThreshold),
      testid: 'tournament-card-soglia',
    });
  }

  /*
   * 3. I POSTI, e la differenza col piede della card e' tutta qui: li' c'e'
   * quanti sono ADESSO, qui quanti ne servono e quanti ce ne stanno.
   *
   * Solo programmato: a torneo avviato il minimo e' gia' stato verificato — o
   * il torneo si sarebbe annullato da solo — e il massimo non ammette piu'
   * nessuno. Due numeri che non decidono piu' niente, dodici pixel sopra un
   * conteggio vero, si leggono come rumore.
   *
   * `maxPlayers` puo' mancare sulle righe piu' vecchie di migration 089, e
   * senza il tetto non c'e' un intervallo da scrivere.
   */
  if (programmato && gara.maxPlayers != null) {
    const minimo = gara.minPlayers ?? 2;
    /* «20–20» non e' un intervallo: quando i due estremi coincidono si scrive
       un numero solo. E' gia' la regola del riepilogo nel menu di creazione. */
    celle.push({
      etichetta: t('tournament.card.seats'),
      valore: minimo === gara.maxPlayers ? String(gara.maxPlayers) : `${minimo}–${gara.maxPlayers}`,
      testid: 'tournament-card-posti',
    });
  }

  return celle.slice(0, tetto);
}

/**
 * L'etichetta di una manche in una lista di partite: «Andata», «Ritorno», o
 * niente.
 *
 * Niente e' il caso normale, e va detto: nei tornei a manche unica — la
 * stragrande maggioranza — un accoppiamento e' una partita sola, e scriverci
 * «Andata» accanto sarebbe rumore che promette un ritorno che non ci sara'.
 *
 * Serve invece dove le partite sono due, ed e' il difetto che la migration 105
 * dichiarava di voler chiudere senza chiuderlo: le due righe di una coppia
 * stanno nello STESSO turno, quindi senza etichetta la scheda «Partite» mostra
 * due «R2» identiche e chi legge non sa quale sia quale. Dalla 113 succede
 * anche nei gironi, dove con quattro iscritti l'andata e ritorno e'
 * obbligatoria: non e' piu' un caso di nicchia.
 *
 * Le chiavi sono quelle delle sfide dirette (`online.match.legFirst` /
 * `legSecond`), gia' tradotte in otto lingue: la stessa cosa detta da due
 * chiavi diverge al primo ritocco di una delle due.
 *
 * `leg` fuori da 1 e 2 non puo' esistere — il CHECK `tournament_games_leg_valido`
 * lo impedisce — ma arriva dal filo, quindi qui si tace invece di stampare
 * un'etichetta inventata.
 */
export function etichettaManche(
  t: TFunction,
  leg: number | undefined,
  dueManche: boolean,
): string | null {
  if (!dueManche) return null;
  if (leg === 1) return t('online.match.legFirst');
  if (leg === 2) return t('online.match.legSecond');
  return null;
}

/**
 * Le condizioni con cui si GIOCA, in pastiglie.
 *
 * ⚠️ Prima dell'avvio queste dicono cio' che e' stato CHIESTO, non cio' che
 * sara'. Un girone con quattro iscritti o meno gioca il ritorno comunque
 * (`ritornoObbligatorio`), e il turno extra viene ignorato se il campo non lo
 * regge (`turniSvizzeroPerIscritti`). Dall'avvio in poi la riga in tabella
 * porta la bandiera APPLICATA, e queste pastiglie si correggono da sole senza
 * che nessuno debba ricordarsene: e' il motivo per cui leggono i campi del
 * server invece di rifare il conto.
 */
export function condizioniDiGioco(t: TFunction, gara: FattiDiUnaGara): Condizione[] {
  const condizioni: Condizione[] = [];

  /* L'apertura sorteggiata. Il perche' della degradazione per variante sta su
     `pastigliaApertura`, che e' anche l'unico posto dove vive. */
  const apertura = pastigliaApertura(t, gara.openingMode, gara.variant, gara.openingId);
  if (apertura !== null) {
    condizioni.push({ testo: apertura, testid: 'tournament-card-apertura' });
  }

  /*
   * ANDATA E RITORNO: una parola, e una vecchia che vale ancora per la storia.
   *
   * `twoLeg` e' la parola di adesso, e dalla migration 113 vale per tutti e
   * tre i formati: lo stesso accoppiamento rigiocato dentro il turno, a colori
   * invertiti. `doubleRound` e' il girone che ripeteva il CALENDARIO, e
   * nessuno la scrive piu' — ma i gironi FINITI nati prima ce l'hanno, ed e'
   * l'unica cosa che li etichetta «andata e ritorno». L'OR resta finche' resta
   * la colonna, e non puo' doppiare: una riga ha l'una o l'altra, mai tutt'e
   * due.
   *
   * `Boolean(...)` e non `??`: un campo assente durante un deploy — JS nuovo,
   * Node vecchio — vuol dire «taci», mai «probabilmente si'».
   */
  if (Boolean(gara.twoLeg) || Boolean(gara.doubleRound)) {
    /* La parola e' quella delle arene, e resta una sola: la stessa cosa detta
       da due chiavi diverge al primo ritocco di una delle due, e otto file per
       ripetere «Andata/ritorno» sarebbero un costo senza guadagno. */
    condizioni.push({ testo: t('arena.twoLeg'), testid: 'tournament-card-due-manche' });
  }

  /*
   * I TURNI EXTRA, e solo finche' il torneo e' programmato.
   *
   * Dopo l'avvio i turni in piu' sono gia' DENTRO `total_rounds`, e «turno 2 di
   * 6» lo dice meglio di quanto possa dirlo una promessa. Prima dell'avvio e'
   * appunto una promessa che il server puo' non mantenere, ed e' onesto
   * mostrarla solo dove e' ancora una richiesta.
   *
   * La pastiglia porta il NUMERO e non l'etichetta del campo: diceva «Turno
   * extra (svizzero)», che con la tendina a tre voci non distingue piu' un
   * turno da due — ed e' la differenza fra quello che si e' chiesto e quello
   * che si legge. Sono le stesse voci della tendina, cosi' chi ha creato il
   * torneo rilegge la parola che ha scelto.
   *
   * La soglia nella condizione e' una rete: il CHECK della 116 lega gia' i due
   * campi, ma una riga che arrivasse slegata scriverebbe una pastiglia su un
   * formato che i turni extra non ce li ha.
   */
  const turniExtra = gara.swissExtraRounds ?? 0;
  if (turniExtra > 0 && gara.status === 'scheduled' && gara.swissThreshold != null) {
    condizioni.push({
      testo: turniExtra >= 2
        ? t('tournament.create.extraRoundsTwo')
        : t('tournament.create.extraRoundsOne'),
      testid: 'tournament-card-turno-extra',
    });
  }

  return condizioni;
}
